import { PortableText } from "next-sanity";
import type { IntrinsicElement } from "@/utils/jsx";
import { slugify } from "@/utils/slugify";
import { Elements, HeadingLevels, ModuleProps } from "@/utils/types";
import { ImageObject } from "../modules/ImageObject";

export interface PortableProps extends ModuleProps {
  content: any[];
  gap?: string;
  headingClasses?: string;
  elementTag?: Elements;
}

export const HeadingItem = ({
  children,
  className,
  headingLevel,

  componentId,
}: {
  children: string;
  className?: string;

  headingLevel: HeadingLevels;
  componentId?: string;
}) => {
  const HeadingTag = (
    headingLevel === 0 ? "p" : `h${headingLevel}`
  ) as IntrinsicElement;

  return (
    <HeadingTag
      id={componentId}
      className={`headingItem text-primaryDark leading-[1] text-[35px] font-display${className ? ` ${className}` : ""}`}
    >
      <span>{children}</span>
    </HeadingTag>
  );
};

export const Portable = ({
  content,
  className,
  componentId,
  elementTag,
  gap = "gap-8",
  headingClasses,
}: PortableProps) => {
  const elm = elementTag || "div";
  const Element = elm as IntrinsicElement;

  const customBlockComponents = {
    types: {
      image: ({ value }: { value: any }) => {
        return (
          <div className="articleImage">
            <ImageObject {...value} alt="" imageWidth={600} />
          </div>
        );
      },
    },
    marks: {
      link: ({
        children,
        value,
      }: {
        children: React.ReactNode;
        value: any;
      }) => {
        const rel = value.href?.startsWith("http")
          ? "noopener noreferrer"
          : undefined;
        return (
          <a
            href={value.href}
            rel={rel}
            target={value.href?.startsWith("http") ? "_blank" : undefined}
            className="font-extrabold"
          >
            {children}
          </a>
        );
      },
    },
    block: {
      h1: ({ children }: { children: string }) => (
        <HeadingItem
          headingLevel={1}
          componentId={slugify(children[0])}
          className={`text-pretty ${headingClasses ? ` ${headingClasses}` : ""}`}
        >
          {children}
        </HeadingItem>
      ),
      h2: ({ children }: { children: string }) => (
        <HeadingItem
          headingLevel={2}
          componentId={slugify(children[0])}
          className={`text-pretty ${headingClasses ? ` ${headingClasses}` : ""}`}
        >
          {children}
        </HeadingItem>
      ),
      h3: ({ children }: { children: string }) => (
        <HeadingItem
          headingLevel={3}
          componentId={slugify(children[0])}
          className={`text-pretty ${headingClasses ? ` ${headingClasses}` : ""}`}
        >
          {children}
        </HeadingItem>
      ),
      h4: ({ children }: { children: string }) => (
        <HeadingItem
          headingLevel={4}
          componentId={slugify(children[0])}
          className={`text-pretty ${headingClasses ? ` ${headingClasses}` : ""}`}
        >
          {children}
        </HeadingItem>
      ),
      h5: ({ children }: { children: string }) => (
        <HeadingItem
          headingLevel={5}
          componentId={slugify(children[0])}
          className={`text-pretty ${headingClasses ? ` ${headingClasses}` : ""}`}
        >
          {children}
        </HeadingItem>
      ),
      h6: ({ children }: { children: string }) => (
        <HeadingItem
          headingLevel={6}
          componentId={slugify(children[0])}
          className={`text-pretty ${headingClasses ? ` ${headingClasses}` : ""}`}
        >
          {children}
        </HeadingItem>
      ),
      normal: ({ children }: { children: string }) => (
        <p className="text-pretty">{children}</p>
      ),
    },

    list: {
      bullet: ({ children }: { children: string }) => (
        <ul className="list-disc list-inside">{children}</ul>
      ),
      number: ({ children }: { children: string }) => (
        <ol className="list-decimal list-inside">{children}</ol>
      ),
    },
  } as any;

  return (
    <Element
      id={componentId}
      className={`portable${className ? ` ${className}` : ""} flex flex-col ${gap}`}
    >
      <PortableText value={content} components={customBlockComponents} />
    </Element>
  );
};
